import { convex } from "@/lib/convexClient";
import { indexedDBService } from "@/services/indexedDBService";
import { StorageServiceFactory } from "@/services/storage/StorageServiceFactory";
import { StorageService } from "@/services/storageService";
import { BunnyStorageClient, isBunnyConfigured } from "@/services/storage/BunnyStorageClient";
import { api } from "../../convex/_generated/api";
import { isElectron } from "@/services/storage/LocalFileSystemClient";
import { removeProject as removeLocalProject } from "@/data/adapters/local/projects";

/**
 * Comprehensive project storage cleanup
 * Deletes all files associated with a project from storage
 */
export const cleanupProjectStorage = async (
  projectId: string,
  craftspaceId: string,
  storageSegments?: { craftspaceKey: string; projectKey: string },
): Promise<{
  success: boolean;
  deletedFiles: number;
  error?: string;
}> => {
  try {
    console.log(`🗑️ Starting storage cleanup for project ${projectId} in craftspace ${craftspaceId}`);

    // In Electron, delete the project folder from local disk
    if (isElectron()) {
      const basePath = await window.electronAPI!.getStoragePath();
      const projectFolder = `${basePath}/${storageSegments?.craftspaceKey ?? craftspaceId}/${
        storageSegments?.projectKey ?? projectId
      }`;
      await window.electronAPI!.deleteFolder(projectFolder);
      console.log(`✅ Deleted local project folder: ${projectFolder}`);
      return { success: true, deletedFiles: 1 };
    }

    let totalDeletedFiles = 0;

    // Get the storage service for the craftspace
    const storageProvider = await StorageServiceFactory.getCraftspaceStorageProvider(craftspaceId);

    if (storageProvider === "custom-s3") {
      // S3 cleanup
      const storageService = await StorageServiceFactory.createStorageService(craftspaceId);
      if (!storageService) {
        throw new Error("S3 storage service not available");
      }

      // Delete all files in the project folder
      const projectPath = `${storageSegments?.craftspaceKey ?? craftspaceId}/${
        storageSegments?.projectKey ?? projectId
      }`;
      const deletedCount = await storageService.deleteFolder(projectPath);
      totalDeletedFiles = deletedCount;
    } else if (isBunnyConfigured()) {
      // Bunny: delete the entire project folder in one API call
      const projectFolder = `${storageSegments?.craftspaceKey ?? craftspaceId}/${
        storageSegments?.projectKey ?? projectId
      }`;
      const { error } = await BunnyStorageClient.removeFolder(projectFolder);
      if (error) {
        console.warn(`Failed to delete Bunny project folder: ${error.message}`);
      } else {
        totalDeletedFiles = 1; // folder deleted
      }
    } else {
      // 360crafter cloud storage cleanup
      const projectBase = `${storageSegments?.craftspaceKey ?? craftspaceId}/${
        storageSegments?.projectKey ?? projectId
      }`;
      const projectPaths = [projectBase];

      const filesToDelete: string[] = [];
      for (const basePath of projectPaths) {
        await collectFilesRecursively(basePath, filesToDelete);
      }

      if (filesToDelete.length > 0) {
        const batchSize = 100;
        for (let i = 0; i < filesToDelete.length; i += batchSize) {
          const batch = filesToDelete.slice(i, i + batchSize);
          try {
            await StorageService.removeFiles("staging", batch);
            totalDeletedFiles += batch.length;
          } catch (error) {
            console.warn(`Failed to delete batch ${i}-${i + batchSize}:`, error);
          }
        }
      }
    }

    console.log(`✅ Storage cleanup completed: deleted ${totalDeletedFiles} files`);
    return {
      success: true,
      deletedFiles: totalDeletedFiles,
    };
  } catch (error) {
    console.error("❌ Storage cleanup failed:", error);
    return {
      success: false,
      deletedFiles: 0,
      error: error instanceof Error ? error.message : "Unknown error",
    };
  }
};

/**
 * Recursively collect all files in a storage path
 */
async function collectFilesRecursively(path: string, files: string[]): Promise<void> {
  try {
    const data = await StorageService.listFiles("staging", path);

    if (!data || data.length === 0) return;

    for (const item of data) {
      // Handle both Bunny (ObjectName, IsDirectory) and Supabase (name/id, metadata.size) formats
      const itemName = item.ObjectName || item.name || item.id;
      if (!itemName || itemName.startsWith(".")) continue;

      const fullPath = `${path}/${itemName}`;

      const isDirectory =
        item.IsDirectory !== undefined
          ? item.IsDirectory
          : item.metadata?.size === undefined;

      if (isDirectory) {
        await collectFilesRecursively(fullPath, files);
      } else {
        files.push(fullPath);
      }
    }
  } catch (error) {
    console.warn(`Error collecting files from ${path}:`, error);
  }
}

/**
 * Complete project deletion with storage and database cleanup
 */
export const deleteProjectCompletely = async (
  projectId: string,
  craftspaceId: string,
  storageSegments?: { craftspaceKey: string; projectKey: string },
): Promise<{
  success: boolean;
  deletedFiles: number;
  error?: string;
}> => {
  try {
    console.log(`🗑️ Starting complete deletion of project ${projectId}`);

    // 1. Clean up storage files
    const storageCleanup = await cleanupProjectStorage(projectId, craftspaceId, storageSegments);

    // 2. Clean up IndexedDB cache
    try {
      await indexedDBService.clearProjectCache(projectId);
      console.log(`✅ Cleared IndexedDB cache for project ${projectId}`);
    } catch (error) {
      console.warn("Failed to clear IndexedDB cache:", error);
    }

    // 3. Delete project record from database.
    if (isElectron()) {
      await removeLocalProject(projectId, craftspaceId);
      console.log(`✅ Local project deleted: ${projectId}`);
      return {
        success: true,
        deletedFiles: storageCleanup.deletedFiles,
      };
    }

    try {
      await convex.mutation(api.projects.remove, { id: projectId });

      console.log(`✅ Convex project deleted: ${projectId}`);
      return {
        success: true,
        deletedFiles: storageCleanup.deletedFiles,
      };
    } catch (convexError) {
      console.warn("Convex project deletion failed, attempting legacy fallback path:", convexError);
    }

    // 4. Legacy UUID project IDs are no longer deleted in-app.
    const looksLikeUuid =
      /^[0-9a-f]{8}-[0-9a-f]{4}-[1-5][0-9a-f]{3}-[89ab][0-9a-f]{3}-[0-9a-f]{12}$/i.test(projectId);
    if (!looksLikeUuid) {
      throw new Error(
        "Failed to delete Convex project and legacy fallback is not applicable for non-UUID project IDs",
      );
    }
    throw new Error(
      "Legacy UUID project deletion is no longer supported in-app. Migrate the project ID to Convex or remove it manually in the legacy system.",
    );
  } catch (error) {
    console.error("❌ Complete project deletion failed:", error);
    return {
      success: false,
      deletedFiles: 0,
      error: error instanceof Error ? error.message : "Unknown error",
    };
  }
};
